/**
 * Mise en page des photos dans le livre.
 *
 * Taille S (actuelle) : ~3 photos / page. M et L n'existent que si la période
 * choisie tient dans un seul tome (≤ 400 pages en S). Sinon on ne touche à rien,
 * pour ne pas fabriquer un tome de plus.
 *
 * CSS : max-width 100 % + max-height selon la taille (contain). Un paysage
 * s'arrête à la largeur de la bulle, un portrait profite de la hauteur, et aucune
 * image ne sort de son cadre. Les max-height de book.css doivent rester sous les
 * budgets PHOTO_TIER_LINES, sinon les pages débordent en PDF.
 */

export const PHOTO_TIER_LINES = {
	s: 14,
	m: 22,
	l: 28,
};

const PHOTO_TIER_ORDER = ['s', 'm', 'l'];

// Filet avant le plafond d'un tome, pour l'estimation. La pagination réelle tranche.
const PHOTO_LAYOUT_SAFETY_PAGES = 8;

// Aligné sur MESSAGE_ATTACHMENTS_SEPARATOR (ConversationActions) : on ne l'importe
// pas ici pour rester hors du cycle BookActions ↔ ConversationActions.
const PHOTO_ATTACHMENTS_SEPARATOR = '|||';

// Extensions réellement affichées en photo dans le livre : mêmes que displayExtensions
// de getAttachmentUrlData, moins le gif. Une heic ou une bmp part en carte QR, l'agrandir
// réserverait de la place pour rien ; un gif imprimé n'est qu'une image figée, il reste en S.
const PHOTO_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];

function attachmentToRawString(attachment) {
	if(!attachment) {
		return '';
	}
	if(typeof attachment === 'string') {
		return attachment;
	}
	if(typeof attachment === 'object') {
		return `${attachment.filename || attachment.name || attachment.url || attachment.attachment || ''}`;
	}
	return `${attachment}`;
}

/** Nom de fichier nettoyé (CR CSV, query string, espaces). */
export function getAttachmentFilename(attachment) {
	return attachmentToRawString(attachment)
		.replace(/\r/g, '')
		.trim()
		.split(/[?#]/)[0];
}

function listAttachments(attachments) {
	if(!attachments) {
		return [];
	}
	if(Array.isArray(attachments)) {
		return attachments;
	}
	if(typeof attachments === 'string') {
		return attachments.split(PHOTO_ATTACHMENTS_SEPARATOR);
	}
	return [attachments];
}

export function bumpPhotoTier(tier) {
	const index = PHOTO_TIER_ORDER.indexOf(tier);
	if(index < 0 || index === PHOTO_TIER_ORDER.length - 1) {
		return tier === 'l' ? 'l' : 's';
	}
	return PHOTO_TIER_ORDER[index + 1];
}

export function isPhotoAttachment(attachment) {
	const filename = getAttachmentFilename(attachment);
	if(!filename || /^https?:\/\//i.test(filename)) {
		return false;
	}
	const base = filename.split(/[/\\]/).pop() || filename;
	const lastDot = base.lastIndexOf('.');
	if(lastDot < 0) {
		return false;
	}
	return PHOTO_EXTENSIONS.includes(base.slice(lastDot + 1).toLowerCase());
}

export function getImageAttachments(attachments) {
	return listAttachments(attachments).filter(isPhotoAttachment);
}

export function getIsolatedPhotoKey(message, attachment) {
	const identity = message?.reference || `${message?.sender || ''}|${message?.date || ''}`;
	return `${identity}::${getAttachmentFilename(attachment)}`;
}

export function getIsolatedPhotoKeys(messages = []) {
	const keys = new Set();
	messages.forEach((message, index) => {
		const photos = getImageAttachments(message?.attachments);
		if(photos.length !== 1) {
			return;
		}
		const prevHas = getImageAttachments(messages[index - 1]?.attachments).length > 0;
		const nextHas = getImageAttachments(messages[index + 1]?.attachments).length > 0;
		if(!prevHas && !nextHas) {
			keys.add(getIsolatedPhotoKey(message, photos[0]));
		}
	});
	return keys;
}

export function collectPhotoStats(messages = []) {
	let photoCount = 0;
	let isolatedPhotoCount = 0;
	messages.forEach((message, index) => {
		const photos = getImageAttachments(message?.attachments);
		photoCount += photos.length;
		if(photos.length !== 1) {
			return;
		}
		const prevHas = getImageAttachments(messages[index - 1]?.attachments).length > 0;
		const nextHas = getImageAttachments(messages[index + 1]?.attachments).length > 0;
		if(!prevHas && !nextHas) {
			isolatedPhotoCount += 1;
		}
	});
	return { photoCount, isolatedPhotoCount };
}

export function countContentPages(pages = []) {
	return pages.filter(page => (page?.messages || []).length > 0).length;
}

/**
 * Hauteur réservée à une photo, en px. On budgète le plafond de la taille, sans
 * regarder la forme de l'image : ses dimensions ne sont pas connues au moment de
 * la pagination. Un paysage rendu plus bas que son budget laisse donc un peu d'air
 * sous lui, ce qui est le sens sûr (sous-budgéter ferait déborder la page en PDF).
 */
export function getPhotoBudgetPx(tier, fontBase = 10) {
	return fontBase * (PHOTO_TIER_LINES[tier] || PHOTO_TIER_LINES.s);
}

export function resolvePhotoAttachmentLayout(attachment, message, options = {}, fontBase = 10) {
	if(!isPhotoAttachment(attachment)) {
		return { tier: null, px: null };
	}
	let tier = options.photoTier || 's';
	if(options.highlightIsolated && options.isolatedPhotoKeys instanceof Set) {
		if(options.isolatedPhotoKeys.has(getIsolatedPhotoKey(message, attachment))) {
			tier = bumpPhotoTier(tier);
		}
	}
	return { tier, px: getPhotoBudgetPx(tier, fontBase) };
}

function estimateExtraPages(photoCount, fromTier, toTier, pageSize, fontBase) {
	if(!photoCount || fromTier === toTier) {
		return 0;
	}
	const extraPx = photoCount * (PHOTO_TIER_LINES[toTier] - PHOTO_TIER_LINES[fromTier]) * fontBase;
	return extraPx / pageSize;
}

/**
 * Choisit la taille appliquée à toutes les photos du livre. Reste en S si la
 * période dépasse un tome. Le type Best Of n'est plus un skip : un Best Of
 * court a de la marge, un Best Of calibré à 396-399 pages reste en S tout seul
 * via le plafond.
 *
 * M est le plafond global : deux photos M tiennent sur une page, alors qu'une
 * photo L en occupe une à elle seule. Sur une rafale, un L global laisserait
 * donc une page à moitié vide par photo. L est réservé aux photos isolées, qui
 * ont du texte autour pour remplir la page.
 */
export function pickBookPhotoLayout({
	contentPageCount = 0,
	photoCount = 0,
	isolatedPhotoCount = 0,
	pagesPerTome = 400,
	pageSize = 475,
	fontBase = 10,
} = {}) {
	const standard = { photoTier: 's', highlightIsolated: false };
	if(!photoCount || contentPageCount > pagesPerTome) {
		return standard;
	}

	const ceiling = pagesPerTome - PHOTO_LAYOUT_SAFETY_PAGES;
	const fits = extra => contentPageCount + extra <= ceiling;

	const extraM = estimateExtraPages(photoCount - isolatedPhotoCount, 's', 'm', pageSize, fontBase)
		+ estimateExtraPages(isolatedPhotoCount, 's', 'l', pageSize, fontBase);
	if(fits(extraM)) {
		return { photoTier: 'm', highlightIsolated: true };
	}

	const extraIsolated = estimateExtraPages(isolatedPhotoCount, 's', 'm', pageSize, fontBase);
	if(isolatedPhotoCount && fits(extraIsolated)) {
		return { photoTier: 's', highlightIsolated: true };
	}

	return standard;
}

export function photoLayoutFitsTome(pages, pagesPerTome) {
	return countContentPages(pages) <= pagesPerTome;
}
